import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	Checkbox,
	ChevronRightIcon,
	CircleUserIcon,
	ConversationIcon,
	DialogueIcon,
	FlexGap,
	Paragraph,
	Table,
	TargetIcon,
	tokens,
} from '@treams/treams-ui';

import { CycleMomentsPreview } from '../../packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';
import TooltipDark from '../../packages/kakapo-ui-kit/components/tooltips/TooltipDark';
import { CHECKBOX_WIDTH, JOB_WIDTH, NAME_WIDTH } from './InboxManagerTable';

const collapseIn = keyframes`
	from {
		opacity: 0;
		transform: scaleY(0.0);
	}

	to {
		opacity: 1;
		transform: none;
	}
`;

const collapseOut = keyframes`
	from {
		opacity: 1;
		transform: none;
	}

	to {
		opacity: 0;
		transform: scaleY(0.0);
	}
`;

const Collapse = styled(Box)<{ entering: boolean; exiting: boolean }>`
	transform-origin: top center;

	${(props) =>
		props.entering
			? css`
					animation: ${collapseIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1);
				`
			: ``}

	${(props) =>
		props.exiting
			? css`
					animation: ${collapseOut} 0.4s cubic-bezier(0.22, 1, 0.36, 1);
					animation-fill-mode: forwards;
				`
			: ``}
`;

const Detail = styled(Box)`
	background: ${tokens.surface.secondary};
`;

function Clip(props: { text?: string }) {
	const [hover, setHover] = useState(false);

	return (
		<Box
			style={{ position: 'relative' }}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<TooltipDark direction="north" isHovered={hover}>
				{props.text}
			</TooltipDark>
			<Box style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{props.text}</Box>
		</Box>
	);
}

export default function InboxManagerItem(props: {
	user: {
		id: string;
		firstName: string;
		lastName: string;
		avatar?: { source: { url: string } } | null;
	};
	dateRange: [Date, Date];
	details: Map<string, graphql.User>;
	selected?: string;
	setSelected: (selected: string | undefined) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const result = props.user;
	const detail = props.details.get(result.id);
	const selected = props.selected === result.id;

	const usesFeedback = useFeature(graphql.FeatureName.FeedbackAdhoc);
	const usesGoals = useFeature(graphql.FeatureName.Goals);
	const usesEvaluations = useFeature(graphql.FeatureName.Evaluations);

	const jobPosition = detail
		? localize(detail.currentJobPosition?.jobTitle.title ?? [])?.text
		: undefined;

	const toggleSelected = () =>
		selected ? props.setSelected(undefined) : props.setSelected(result.id);

	return (
		<Box width="100%">
			<Table.Row tall selected={selected} onClick={toggleSelected}>
				<Table.Cell width={CHECKBOX_WIDTH}>
					<Box onClick={(event) => event.stopPropagation()}>
						<Checkbox mr={0} checked={selected} onClick={toggleSelected} />
					</Box>
				</Table.Cell>
				<Table.Cell width={NAME_WIDTH}>
					<Avatar size={32} user={detail ?? result} />
					<Clip text={`${result.firstName} ${result.lastName}`} />
				</Table.Cell>
				<Table.Cell width={JOB_WIDTH}>
					{jobPosition ? <Clip text={jobPosition} /> : !detail && <Paragraph.Skeleton lines={1} />}
				</Table.Cell>
				<Table.Cell grow>
					{detail ? (
						<Flex width="100%" flexDirection="column" justifyContent="center" alignItems="stretch">
							<CycleMomentsPreview user={detail} mini dateRange={props.dateRange} />
						</Flex>
					) : (
						<Paragraph.Skeleton lines={1} />
					)}
				</Table.Cell>
			</Table.Row>
			{detail && (
				<Transition in={selected} timeout={400} mountOnEnter unmountOnExit>
					{(state) => (
						<Collapse entering={state === 'entering'} exiting={state === 'exiting'}>
							<Detail
								pl={`${CHECKBOX_WIDTH + tokens.gap.group + 32 + tokens.gap.element}px`}
								pr={3}
								py={3}
							>
								<FlexGap gap={tokens.gap.element} mb={3} flexWrap="wrap">
									<Button
										link={`/users/${detail.id}`}
										title={t('managerInbox.table.goToProfile', { name: result.firstName })}
										icon={<CircleUserIcon />}
										rightIcon={<ChevronRightIcon />}
										small
										type="secondary"
									/>
									{usesFeedback && (
										<Button
											link={`/users/${result.id}/feedback`}
											title={t('managerInbox.table.goToFeedback')}
											icon={<DialogueIcon />}
											rightIcon={<ChevronRightIcon />}
											small
											type="secondary"
										/>
									)}
									{usesGoals && (
										<Button
											title={t('managerInbox.table.goToGoals')}
											link={`/users/${result.id}/goals`}
											icon={<TargetIcon />}
											rightIcon={<ChevronRightIcon />}
											small
											type="secondary"
										/>
									)}
									{usesEvaluations && (
										<Button
											title={t('managerInbox.table.goToEvaluations')}
											link={`/users/${result.id}/conversations`}
											icon={<ConversationIcon />}
											rightIcon={<ChevronRightIcon />}
											small
											type="secondary"
										/>
									)}
								</FlexGap>
								<CycleMomentsPreview user={detail} canSendReminder dateRange={props.dateRange} />
							</Detail>
						</Collapse>
					)}
				</Transition>
			)}
		</Box>
	);
}
